import React, { useState, useEffect } from "react";

import {
  Button,
  ButtonGroup,
  Collapse,
  Grid,
  List,
  ListItemSecondaryAction,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
  Tooltip
} from "@material-ui/core";

import LaunchIcon from "@material-ui/icons/Launch";
import ErrorIcon from "@material-ui/icons/Error";
import FolderIcon from "@material-ui/icons/Folder";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import ExpandLess from "@material-ui/icons/ExpandLess";
import ExpandMore from "@material-ui/icons/ExpandMore";
import DocumentIcon from "@material-ui/icons/Description";

import { useQuery } from "@tanstack/react-query";

import { useSubscription } from "../../../../../framework/src/contexts/Subscription";
import StorageProvider from "../../../../../framework/src/StorageProvider";

import { useStyles } from "./styles";
import { fetchDownloadUrl, fetchSurveyDocuments } from "./queries";

export default function NestedList() {
  const classes = useStyles();
  const [open, setOpen] = React.useState(false);

  const [accountId, setAccountId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<number | null>(null);
  const [documentId, setDocumentId] = useState<number | null>(null);
  const { authToken } = useSubscription();

  useEffect(() => {
    const fetchStorage = async () => {
      const storedAccountId = await StorageProvider.get("accountId");
      const storedClientId = await StorageProvider.get("clientId");
      if (storedAccountId && storedClientId) {
        setAccountId(storedAccountId);
        setClientId(storedClientId);
      }
    };

    fetchStorage();
  }, []);

  const { isLoading, isError, data, refetch, failureCount } = useQuery({
    queryKey: ["survey_documents", accountId, clientId],
    queryFn: () => fetchSurveyDocuments(authToken!, clientId!, accountId!),
    enabled: !!authToken && !!accountId && !!clientId,
    keepPreviousData: true
  });

  useQuery({
    queryKey: ["download_url", documentId],
    queryFn: () => fetchDownloadUrl(authToken!, documentId!),
    enabled: !!authToken && !!documentId,
    keepPreviousData: false,
    onSuccess: res => {
      res.documents.forEach((document: any) => {
        window.open(document.url, "_blank");
      });
    }
  });

  return (
    <Grid container>
      <Grid
        item
        xs={12}
        container
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography className={classes.title} color="primary">
          Documents
        </Typography>
      </Grid>
      <List
        component="nav"
        aria-labelledby="nested-list-subheader"
        className={classes.root}
      >
        <ListItem button onClick={() => setOpen(!open)}>
          <ListItemIcon>
            {isLoading && !isError && <CircularProgress size="small" />}
            {!isLoading && isError && (
              <Tooltip
                title={`An error occurred while fetching documents from this folder. Click to try again. ${failureCount} attempts made.`}
                onClick={() => refetch()}
              >
                <ErrorIcon />
              </Tooltip>
            )}
            {!isLoading && !isError && open ? (
              <FolderOpenIcon />
            ) : (
              <FolderIcon />
            )}
          </ListItemIcon>
          <ListItemText
            className={classes.listItem}
            primary="Onboarding Documents"
          />
          {!isLoading && !isError && open ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {!isLoading &&
              !isError &&
              data.data.map((document: any) => (
                <ListItem button key={document.id} className={classes.nested}>
                  <ListItemIcon>
                    <DocumentIcon />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.listItem}
                    primary={document.attributes.document_template_title}
                  />
                  <ListItemSecondaryAction>
                    <ButtonGroup size="large" color="primary">
                      {document.attributes.docuseal_submission_id &&
                        document.attributes.docuseal_submission_slug && (
                          <Button
                            variant="contained"
                            color="default"
                            onClick={() => {
                              setDocumentId(document.id);
                            }}
                            className={classes.button}
                            startIcon={<LaunchIcon />}
                          >
                            Download
                          </Button>
                        )}
                      {!document.attributes.docuseal_submission_id &&
                        !document.attributes.docuseal_submission_slug && (
                          <Button
                            variant="contained"
                            color="default"
                            target="_blank"
                            href={document.attributes.document}
                            className={classes.button}
                            startIcon={<LaunchIcon />}
                          >
                            View
                          </Button>
                        )}
                    </ButtonGroup>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
          </List>
        </Collapse>
      </List>
    </Grid>
  );
}
